import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addDays, startOfDay } from 'date-fns';
import { RootState, AppDispatch } from '../store';
import { useTranslation } from '../hooks/useTranslation';
import { fetchWeeklyView, toggleRecord, WeeklyHabitData } from '../store/slices/recordsSlice';
import { fetchHabits, Habit } from '../store/slices/habitsSlice';
import {
  toggleSidebar,
  openAddHabitModal,
  showAchievementAnimation,
  hideAchievementAnimation,
} from '../store/slices/uiSlice';
import { playSuccessSound } from '../assets/sounds/success-sound';
import { trackPageView, trackHabitCompleted } from '../hooks/useActivityTracking';
import { badgeService, Badge, UserStats } from '../services/badgeService';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { getLocalDateString } from '../utils/dateUtils';
import { rateLimiter } from '../utils/security';
import { db } from '../utils/db';
import { calculateGlobalStats } from '../utils/habitUtils';

import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import WeekHeader from '../components/WeekHeader';
import HabitList from '../components/HabitList';
import AddHabitModal from '../components/AddHabitModal';
import MonthlyView from '../components/MonthlyView';
import AchievementAnimation from '../components/AchievementAnimation';
import BadgeUnlockAnimation from '../components/BadgeUnlockAnimation';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

import '../styles/MainView.css';

const MainView: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { weeklyView, isLoading: recordsLoading, isUpdating } = useSelector((state: RootState) => state.records);
  const { habits, isLoading: habitsLoading } = useSelector((state: RootState) => state.habits);
  
  const [selectedHabit, setSelectedHabit] = useState<WeeklyHabitData | null>(null);
  const [unlockedBadge, setUnlockedBadge] = useState<Badge | null>(null);
  const { showPrompt: showPWAPrompt, hidePrompt: hidePWAPrompt } = usePWAInstall();
  const achievementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    isSidebarOpen,
    isAddHabitModalOpen,
    achievementAnimation,
    soundEnabled,
  } = useSelector((state: RootState) => state.ui);

  useEffect(() => {
    return () => {
      if (achievementTimerRef.current !== null) {
        clearTimeout(achievementTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    trackPageView('main');
    dispatch(hideAchievementAnimation());
    
    dispatch(fetchHabits());
    dispatch(fetchWeeklyView());
  }, [dispatch]);

  // weeklyViewが更新されたら、選択中の習慣データも同期する（カレンダーを閉じないため）
  useEffect(() => {
    if (selectedHabit && weeklyView) {
      const updated = weeklyView.habits.find((h) => h.id === selectedHabit.id);
      if (updated) {
        setSelectedHabit(updated);
      }
    }
  }, [weeklyView, selectedHabit]);

  // 統計計算ロジック
  const calculateUserStats = async (): Promise<UserStats> => {
    const globalStats = await calculateGlobalStats();
    const currentStats = badgeService.getStats();
    
    return {
      ...currentStats,
      ...globalStats,
      id: 'current',
      userId: currentStats.userId,
    };
  };

  const handleRecordClick = async (habitId: string, date: string) => {
    const actionKey = `record-${habitId}-${date}`;
    if (!rateLimiter.canPerformAction(actionKey)) {
      return;
    }
    
    const habit = weeklyView?.habits.find((h) => h.id === habitId);
    const previousRecord = habit?.records.find((r) => r.date === date);
    const previousStatus = previousRecord?.status || null;

    const result = await dispatch(toggleRecord({ habitId, date })).unwrap();
    
    // 即座に再取得（カレンダーが表示されていても HabitList は unmount されない）
    dispatch(fetchWeeklyView());
    
    const newStatus = result.status;

    // 達成アニメーション判定
    if (previousStatus !== 'completed' && newStatus === 'completed') {
      const stats = await calculateUserStats();
      await badgeService.updateStats(stats);
      const newBadges = await badgeService.checkBadgeProgress(stats);
      
      if (newBadges.length > 0) {
        setUnlockedBadge(newBadges[0]);
      } else {
        dispatch(showAchievementAnimation({ habitId, date }));
        if (achievementTimerRef.current !== null) {
          clearTimeout(achievementTimerRef.current);
        }
        achievementTimerRef.current = setTimeout(() => {
          dispatch(hideAchievementAnimation());
          achievementTimerRef.current = null;
        }, 3000);
      }
      
      trackHabitCompleted(habitId, date);
      
      if (soundEnabled) {
        playSuccessSound(0.5).catch(() => {});
      }
    }
  };

  const getDates = () => {
    const dates = [];
    const today = startOfDay(new Date());
    for (let i = -9; i <= 0; i++) {
      dates.push(addDays(today, i));
    }
    return dates;
  };

  const handleMonthlyViewOpen = (habit: WeeklyHabitData) => {
    setSelectedHabit(habit);
  };

  return (
    <div className="main-view">
      <Header 
        onMenuClick={() => dispatch(toggleSidebar())}
        onAddClick={() => dispatch(openAddHabitModal())}
      />
      
      <Sidebar 
        isOpen={isSidebarOpen}
        onClose={() => dispatch(toggleSidebar())}
      />
      
      <div className="main-content">
        <WeekHeader dates={getDates()} />
        
        {(!weeklyView && (recordsLoading || habitsLoading)) ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <HabitList 
            weeklyView={weeklyView}
            dates={getDates()}
            onRecordClick={handleRecordClick}
            onMonthlyViewClick={handleMonthlyViewOpen}
            isUpdating={isUpdating}
          />
        )}
      </div>
      
      {isAddHabitModalOpen && <AddHabitModal />}
      
      {selectedHabit && (
        <MonthlyView
          habitId={selectedHabit.id}
          habitName={selectedHabit.name}
          habitIcon={selectedHabit.icon || '📝'}
          habitColor={selectedHabit.color}
          records={selectedHabit.records}
          onClose={() => setSelectedHabit(null)}
        />
      )}
      
      {achievementAnimation && (
        <AchievementAnimation
          habitId={achievementAnimation.habitId}
          date={achievementAnimation.date}
        />
      )}
      
      {unlockedBadge && (
        <BadgeUnlockAnimation
          badge={unlockedBadge}
          onComplete={() => {
            setUnlockedBadge(null);
            if (achievementAnimation) {
              // バッジ演出の後に達成演出を表示（もしあれば）
            }
          }}
        />
      )}

      {showPWAPrompt && (
        <PWAInstallPrompt onClose={hidePWAPrompt} />
      )}
    </div>
  );
};

export default MainView;
